import { tokenVar } from '../../theme/tokenVar'
import type { ColorScheme } from '../../types'

const toVar = (t: string) => tokenVar(`colors.${t}`)

const tokens = {
  dark: {
    bg: 'bg.input',
    text: 'text.primary',
    // Placeholder matches the value color so all trigger fields in a form
    // read identically (product decision — see MultiSelectInput).
    placeholder: 'text.primary',
    border: 'interactive.tertiary-border',
    optionHover: 'interactive.tertiary-hover-bg',
    focusRing: 'border.input-focus',
    menuBg: 'bg.input',
    iconSecondary: 'icon.secondary',
  },
  light: {
    bg: 'light.bg.input',
    text: 'light.text.primary',
    placeholder: 'light.text.primary',
    border: 'light.interactive.tertiary-border',
    optionHover: 'light.interactive.tertiary-hover-bg',
    focusRing: 'light.border.input-focus',
    menuBg: 'light.bg.card',
    iconSecondary: 'light.icon.secondary',
  },
} as const

// The menu can portal outside DarkProvider, so the font must be explicit —
// it otherwise falls back to the host page's default.
const soehne = "'Soehne', sans-serif"

export const createSelectStyles = (colorScheme: ColorScheme = 'dark') => {
  const t = tokens[colorScheme]
  // Resolved once here rather than inside the react-select callbacks, which
  // run on every interaction.
  const text = toVar(t.text)
  const placeholder = toVar(t.placeholder)
  const optionHover = toVar(t.optionHover)
  const menuBg = toVar(t.menuBg)
  const iconSecondary = toVar(t.iconSecondary)
  return {
    control: (base: any, state: any) => ({
      ...base,
      backgroundColor: 'transparent',
      border: 'none',
      boxShadow: 'none',
      // 26px control + 8px container padding + 2px border = the shared 36px
      // field height used by TextInput.
      minHeight: '26px',
      cursor: state.isDisabled ? 'not-allowed' : 'pointer',
      '&:hover': { border: 'none' },
    }),
    valueContainer: (base: any) => ({ ...base, padding: '0' }),
    placeholder: (base: any) => ({
      ...base,
      color: placeholder,
      fontFamily: soehne,
      fontSize: '11px',
      fontWeight: 400,
      margin: 0,
    }),
    singleValue: (base: any) => ({
      ...base,
      color: text,
      fontFamily: soehne,
      fontSize: '11px',
      margin: 0,
    }),
    input: (base: any) => ({
      ...base,
      color: text,
      fontFamily: soehne,
      fontSize: '11px',
      margin: 0,
      padding: 0,
    }),
    indicatorsContainer: (base: any) => ({ ...base, padding: '0' }),
    dropdownIndicator: (base: any) => ({
      ...base,
      padding: '0 2px',
      color: iconSecondary,
      '&:hover': { color: iconSecondary },
    }),
    clearIndicator: (base: any) => ({
      ...base,
      padding: '0 2px',
      color: iconSecondary,
      cursor: 'pointer',
      '&:hover': { color: iconSecondary },
    }),
    indicatorSeparator: () => ({ display: 'none' }),
    menu: (base: any) => ({
      ...base,
      backgroundColor: menuBg,
      // Sharp corners, matching every other dropdown surface.
      borderRadius: '0px',
      zIndex: 9999,
      marginTop: '4px',
    }),
    menuList: (base: any) => ({ ...base, padding: '4px' }),
    option: (base: any, state: any) => ({
      ...base,
      backgroundColor: state.isFocused ? optionHover : 'transparent',
      color: text,
      fontFamily: soehne,
      fontSize: '11px',
      fontWeight: 400,
      borderRadius: '0px',
      cursor: 'pointer',
      '&:active': { backgroundColor: optionHover },
    }),
    noOptionsMessage: (base: any) => ({
      ...base,
      color: placeholder,
      fontFamily: soehne,
      fontSize: '11px',
    }),
    menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
  }
}

export const selectBoxTokens = (colorScheme: ColorScheme = 'dark') =>
  tokens[colorScheme]

const primaryActionTokens = {
  dark: {
    menuDivider: 'border.menu-divider',
    bgSectionHeader: 'transparent',
    textSecondary: 'text.secondary',
    textPrimary: 'text.primary',
    optionHover: 'interactive.tertiary-hover-bg',
  },
  light: {
    menuDivider: 'light.border.menu-divider',
    bgSectionHeader: 'light.bg.section-header',
    textSecondary: 'light.text.secondary',
    textPrimary: 'light.text.primary',
    optionHover: 'light.interactive.tertiary-hover-bg',
  },
} as const

export const primaryActionTokensFor = (colorScheme: ColorScheme = 'dark') =>
  primaryActionTokens[colorScheme]
